import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Search, FileText, Printer, Eye, Clock,
  IndianRupee, Receipt, Calendar, CreditCard, MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const POSBillsPage = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({ today_count: 0, today_total: 0, total_invoices: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    date_from: '',
    date_to: '',
    status: '',
    payment_mode: ''
  });

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid) fetchInvoices();
  }, [bid, filters.status, filters.payment_mode]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.status) params.status = filters.status;
      if (filters.payment_mode) params.payment_mode = filters.payment_mode;

      const { data } = await api.get('/pos/invoices', { params });
      setInvoices(data.invoices || []);
      if (data.stats) setStats(data.stats);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load invoices'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (invoiceId) => {
    try {
      const { data } = await api.get(`/pos/invoices/${invoiceId}/receipt`);
      const invoice = data.invoice;

      const receiptWindow = window.open('', '_blank', 'width=300,height=600');
      receiptWindow.document.write(`
        <html><head><title>Receipt</title>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; width: 280px; margin: 0 auto; padding: 10px; }
          .center { text-align: center; }
          .right { text-align: right; }
          .bold { font-weight: bold; }
          .line { border-top: 1px dashed #000; margin: 8px 0; }
          .row { display: flex; justify-content: space-between; margin: 2px 0; }
        </style></head><body>
        <div class="center bold">${data.business?.name || 'RECEIPT'}</div>
        <div class="center">${invoice.invoice_number}</div>
        <div class="center">${invoice.invoice_date} ${invoice.invoice_time || ''}</div>
        <div class="line"></div>
        <div>Customer: ${invoice.customer_name}</div>
        <div class="line"></div>
        ${invoice.items?.map(item => `
          <div>${item.item_name}</div>
          <div class="row"><span>${item.quantity} x ₹${item.price}</span><span>₹${item.amount?.toFixed(2)}</span></div>
        `).join('')}
        <div class="line"></div>
        <div class="row bold"><span>TOTAL</span><span>₹${invoice.grand_total?.toFixed(2)}</span></div>
        <div class="row"><span>Paid</span><span>₹${invoice.total_paid?.toFixed(2)}</span></div>
        <div class="line"></div>
        <div class="center">Thank you!</div>
        </body></html>
      `);
      receiptWindow.document.close();
      receiptWindow.print();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error printing receipt'));
    }
  };

  const formatDateTime = (dateStr, timeStr) => {
    const date = new Date(dateStr);
    let formatted = date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    if (timeStr) formatted += ` ${timeStr}`;
    return formatted;
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  const getStatusBadge = (invoice) => {
    const ps = invoice.payment_status || invoice.status;
    const badges = {
      paid: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paid' },
      PAID: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paid' },
      partial: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Partial' },
      PARTIAL: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Partial' },
      unpaid: { bg: 'bg-red-100', text: 'text-red-700', label: 'Unpaid' },
      FINAL: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paid' },
    };
    const badge = badges[ps] || badges.paid;
    return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.bg} ${badge.text}`}>{badge.label}</span>;
  };

  const getPaymentModeBadge = (payments) => {
    const mode = payments?.[0]?.mode || 'CASH';
    const modeStyles = {
      CASH: 'bg-emerald-100 text-emerald-700',
      UPI: 'bg-purple-100 text-purple-700',
      CARD: 'bg-blue-100 text-blue-700',
      BANK: 'bg-slate-100 text-slate-700',
    };
    const style = modeStyles[mode] || 'bg-slate-100 text-slate-700';
    return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${style}`}>{mode}</span>;
  };

  return (
    <div className="h-full flex flex-col bg-[#F7F8FA]">
      {/* Header */}
      <div className="flex-shrink-0 bg-[#F7F8FA] px-6 pt-5 pb-4">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(`/business/${bid}/pos`)} className="p-2 hover:bg-slate-200 rounded-lg">
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-slate-800">POS Bills</h1>
                <p className="text-[13px] text-slate-500 mt-0.5">View and print POS invoices</p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/business/${bid}/pos`)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-[13px] font-medium shadow-sm"
            >
              <Receipt className="w-4 h-4" /> Back to POS
            </button>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center">
                <Receipt className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Today's Bills</p>
                <p className="text-[14px] font-semibold text-slate-700">{stats.today_count}</p>
              </div>
            </div>
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-emerald-50 flex items-center justify-center">
                <IndianRupee className="w-3.5 h-3.5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Today's Sales</p>
                <p className="text-[14px] font-semibold text-emerald-600">{formatCurrency(stats.today_total)}</p>
              </div>
            </div>
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center">
                <FileText className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Total Invoices</p>
                <p className="text-[14px] font-semibold text-slate-700">{stats.total_invoices}</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 relative min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by invoice number or customer..."
                value={filters.search}
                onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && fetchInvoices()}
                className="w-full max-w-md pl-9 pr-4 py-2 bg-white rounded-lg text-[13px] shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
              />
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))}
              className="px-3 py-2 bg-white rounded-lg text-[13px] shadow-sm focus:outline-none"
            >
              <option value="">All Status</option>
              <option value="PAID">Paid</option>
              <option value="PARTIAL">Partial</option>
            </select>
            <select
              value={filters.payment_mode}
              onChange={(e) => setFilters(p => ({ ...p, payment_mode: e.target.value }))}
              className="px-3 py-2 bg-white rounded-lg text-[13px] shadow-sm focus:outline-none"
            >
              <option value="">All Payments</option>
              <option value="CASH">Cash</option>
              <option value="UPI">UPI</option>
              <option value="CARD">Card</option>
              <option value="BANK">Bank</option>
            </select>
            <div className="flex items-center gap-1.5">
              <span className="text-[12px] text-slate-500">From</span>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters(p => ({ ...p, date_from: e.target.value }))}
                className="px-3 py-2 bg-white rounded-lg text-[13px] shadow-sm focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[12px] text-slate-500">To</span>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters(p => ({ ...p, date_to: e.target.value }))}
                className="px-3 py-2 bg-white rounded-lg text-[13px] shadow-sm focus:outline-none"
              />
            </div>
            <button onClick={fetchInvoices} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-[13px] hover:bg-blue-700">
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-hidden px-6 pb-5">
        <div className="max-w-[1600px] mx-auto h-full">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 h-full flex flex-col overflow-hidden">
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
              </div>
            ) : invoices.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center">
                <Receipt className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-[14px] text-slate-500">No POS invoices found</p>
                <p className="text-[12px] text-slate-400 mt-1">Create bills from POS Billing</p>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto">
                  <table className="w-full table-fixed">
                    <colgroup>
                      <col className="w-[14%]" />
                      <col className="w-[16%]" />
                      <col className="w-[20%]" />
                      <col className="w-[14%]" />
                      <col className="w-[12%]" />
                      <col className="w-[12%]" />
                      <col className="w-[12%]" />
                    </colgroup>
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-blue-50/60 border-b border-blue-100">
                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-blue-700 uppercase">Invoice #</th>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-blue-700 uppercase">Date/Time</th>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-blue-700 uppercase">Customer</th>
                        <th className="px-4 py-3 text-right text-[11px] font-semibold text-blue-700 uppercase">Amount</th>
                        <th className="px-4 py-3 text-center text-[11px] font-semibold text-blue-700 uppercase">Payment</th>
                        <th className="px-4 py-3 text-center text-[11px] font-semibold text-blue-700 uppercase">Status</th>
                        <th className="px-4 py-3 text-center text-[11px] font-semibold text-blue-700 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice, idx) => (
                        <tr key={invoice.id} className={`border-b border-slate-100 hover:bg-blue-50/30 ${idx % 2 ? 'bg-slate-50/50' : ''}`}>
                          <td className="px-4 py-3">
                            <span className="text-[13px] font-medium text-slate-800">{invoice.invoice_number}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[12px] text-slate-600">{formatDateTime(invoice.invoice_date, invoice.invoice_time)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[13px] text-slate-700 truncate block">{invoice.customer_name}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-[13px] font-semibold text-slate-800">{formatCurrency(invoice.grand_total)}</span>
                          </td>
                          <td className="px-4 py-3 text-center">{getPaymentModeBadge(invoice.payments)}</td>
                          <td className="px-4 py-3 text-center">{getStatusBadge(invoice)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="p-1.5 hover:bg-slate-100 rounded-md">
                                    <MoreVertical className="w-4 h-4 text-slate-400" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handlePrint(invoice.id)}>
                                    <Printer className="w-4 h-4 mr-2" /> Print Receipt
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex-shrink-0 px-4 py-3 bg-slate-50 border-t border-slate-200">
                  <p className="text-[12px] text-slate-500">Showing {invoices.length} invoices</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default POSBillsPage;
